<template>
  <div class="courses-tab pa-6">
    <v-card class="content-card elevation-0">
      <v-card-title class="pb-4">
        <div>
          <div class="card-title">Slijedovi Jela</div>
          <div class="card-subtitle">Definirajte redoslijed kojim se jelovnik prikazuje</div>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Toggle for using courses -->
        <div class="courses-toggle-section mb-6">
          <div class="d-flex align-center justify-space-between pa-4 toggle-card">
            <div>
              <div class="toggle-title">Koristi slijedove</div>
              <div class="toggle-subtitle">Aktivirajte upravljanje redoslijedom jela</div>
            </div>
            <v-switch
              v-model="isUsingCourses"
              :loading="saving"
              :disabled="saving"
              color="primary"
              hide-details
              class="mt-0 courses-switch"
              @change="updateIsUsingCourses"
            />
          </div>
        </div>

        <!-- Courses list (only visible when enabled) -->
        <div v-if="isUsingCourses" class="courses-list-section">
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="list-title">Popis Slijedova</div>
            <v-btn
              color="primary"
              class="soft-button"
              @click="addCourse"
            >
              <v-icon left small>mdi-plus</v-icon>
              Dodaj Slijed
            </v-btn>
          </div>

          <!-- Course items -->
          <v-list v-if="courses.length > 0" class="courses-list transparent">
            <draggable
              v-model="courses"
              handle=".drag-handle"
              @end="onDragEnd"
            >
              <v-list-item
                v-for="(course, index) in courses"
                :key="course.id || index"
                class="course-item mb-3"
              >
                <v-list-item-content>
                  <div class="d-flex align-center course-row">
                    <!-- Drag handle -->
                    <v-icon class="drag-handle mr-2" size="20">mdi-drag-vertical</v-icon>

                    <!-- Order badge -->
                    <v-chip
                      small
                      class="order-chip mr-3"
                      color="primary"
                      text-color="white"
                    >
                      {{ index + 1 }}
                    </v-chip>

                    <!-- Name field -->
                    <v-text-field
                      v-model="course.name"
                      label="Naziv"
                      dense
                      outlined
                      hide-details
                      class="course-name-input mr-3"
                    />

                    <!-- Save button (only for new courses) -->
                    <v-btn
                      v-if="!course.saved"
                      class="save-button"
                      :loading="course.saving"
                      :disabled="!canSaveCourse(course)"
                      @click="saveCourse(course)"
                    >
                      <v-icon>mdi-check</v-icon>
                    </v-btn>

                    <!-- Delete button -->
                    <v-btn
                      class="delete-button mr-2"
                      :loading="course.deleting"
                      @click="deleteCourse(course)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>

                    <!-- Arrow buttons -->
                    <div class="d-flex align-center" style="gap: 4px;">
                      <v-btn
                        class="arrow-button"
                        :disabled="index === 0"
                        @click="moveUp(index)"
                      >
                        <v-icon>mdi-chevron-up</v-icon>
                      </v-btn>
                      <v-btn
                        class="arrow-button"
                        :disabled="index === courses.length - 1"
                        @click="moveDown(index)"
                      >
                        <v-icon>mdi-chevron-down</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </draggable>
          </v-list>

          <!-- Empty state -->
          <div v-else class="text-center py-8 empty-state">
            <v-icon size="64" color="grey lighten-2">mdi-format-list-numbered</v-icon>
            <div class="text-h6 mt-4 text--secondary">Nema slijedova</div>
            <div class="text-caption text--secondary mb-4">
              Dodajte prvi slijed kako biste definirali redoslijed jela
            </div>
            <v-btn
              color="primary"
              class="soft-button"
              @click="addCourse"
            >
              <v-icon left small>mdi-plus</v-icon>
              Dodaj Prvi Slijed
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { df } from '@/plugins/firebase'
import draggable from 'vuedraggable'

export default {
  name: 'CoursesTab',

  components: {
    draggable
  },

  props: {
    locationId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      loading: false,
      saving: false,
      isUsingCourses: false,
      courses: []
    }
  },

  computed: {
  },

  created () {
    this.loadLocationData()
  },

  watch: {
    locationId () {
      // Reload data when location changes
      this.loadLocationData()
    }
  },

  methods: {
    async loadLocationData () {
      this.loading = true
      try {
        const locationDoc = await df
          .collection('locations')
          .doc(this.locationId)
          .get()

        if (locationDoc.exists) {
          const data = locationDoc.data()
          const ordermanData = data.orderman || {}

          this.isUsingCourses = ordermanData.is_using_courses || false
          this.courses = (ordermanData.courses || []).map(course => ({
            ...course,
            saved: true,
            saving: false,
            deleting: false
          }))
        }
      } catch (error) {
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri učitavanju podataka',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async updateIsUsingCourses () {
      this.saving = true
      try {
        const locationRef = df.collection('locations').doc(this.locationId)

        await locationRef.update({
          'orderman.is_using_courses': this.isUsingCourses
        })

        this.$root.$emit('show-snackbar', {
          text: this.isUsingCourses
            ? 'Slijedovi aktivirani'
            : 'Slijedovi deaktivirani',
          color: 'success'
        })
      } catch (error) {
        // Revert on error
        this.isUsingCourses = !this.isUsingCourses

        this.$root.$emit('show-snackbar', {
          text: 'Greška pri ažuriranju postavki',
          color: 'error'
        })
      } finally {
        this.saving = false
      }
    },

    addCourse () {
      // ID is based on position (length + 1) * 100
      const nextId = (this.courses.length + 1) * 100

      this.courses.push({
        id: nextId,
        name: '',
        saved: false,
        saving: false,
        deleting: false
      })
    },

    canSaveCourse (course) {
      // Check if name is not empty
      return course.name && course.name.trim() !== ''
    },

    recalculateIds () {
      // Recalculate IDs based on current position
      this.courses.forEach((course, index) => {
        course.id = (index + 1) * 100
      })
    },

    async saveCourse (course) {
      if (!this.canSaveCourse(course)) {
        this.$root.$emit('show-snackbar', {
          text: 'Unesite naziv slijeda',
          color: 'warning'
        })
        return
      }

      this.$set(course, 'saving', true)

      try {
        const locationRef = df.collection('locations').doc(this.locationId)

        // Mark as saved
        course.saved = true

        // Update Firestore with all courses
        const coursesToSave = this.courses
          .filter(c => c.saved)
          .map(c => ({
            id: c.id,
            name: c.name
          }))

        await locationRef.update({
          'orderman.courses': coursesToSave
        })

        this.$root.$emit('show-snackbar', {
          text: 'Slijed spremljen',
          color: 'success'
        })
      } catch (error) {
        course.saved = false

        this.$root.$emit('show-snackbar', {
          text: 'Greška pri spremanju slijeda',
          color: 'error'
        })
      } finally {
        this.$set(course, 'saving', false)
      }
    },

    async deleteCourse (course) {
      this.$set(course, 'deleting', true)

      try {
        // Remove from local array
        const index = this.courses.indexOf(course)
        if (index > -1) {
          this.courses.splice(index, 1)
        }

        // Recalculate IDs after deletion
        this.recalculateIds()

        // Update Firestore
        const locationRef = df.collection('locations').doc(this.locationId)

        const coursesToSave = this.courses
          .filter(c => c.saved)
          .map(c => ({
            id: c.id,
            name: c.name
          }))

        await locationRef.update({
          'orderman.courses': coursesToSave
        })

        this.$root.$emit('show-snackbar', {
          text: 'Slijed obrisan',
          color: 'success'
        })
      } catch (error) {
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri brisanju slijeda',
          color: 'error'
        })

        // Reload data to restore state
        this.loadLocationData()
      }
    },

    moveUp (index) {
      if (index === 0) return

      const item = this.courses[index]
      this.courses.splice(index, 1)
      this.courses.splice(index - 1, 0, item)

      // Recalculate IDs and save
      this.recalculateIds()
      this.saveCoursesOrder()
    },

    moveDown (index) {
      if (index === this.courses.length - 1) return

      const item = this.courses[index]
      this.courses.splice(index, 1)
      this.courses.splice(index + 1, 0, item)

      // Recalculate IDs and save
      this.recalculateIds()
      this.saveCoursesOrder()
    },

    onDragEnd () {
      // Recalculate IDs based on new positions
      this.recalculateIds()
      // Update order in Firestore after drag
      this.saveCoursesOrder()
    },

    async saveCoursesOrder () {
      try {
        const locationRef = df.collection('locations').doc(this.locationId)

        // Save courses in current array order
        const coursesToSave = this.courses
          .filter(c => c.saved)
          .map(c => ({
            id: c.id,
            name: c.name
          }))

        await locationRef.update({
          'orderman.courses': coursesToSave
        })
      } catch (error) {
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri ažuriranju redoslijeda',
          color: 'error'
        })
      }
    }
  }
}
</script>

<style scoped>
.courses-tab {
  min-height: 400px;
}

.content-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(0, 179, 190, 0.1) !important;
}

.card-title {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  letter-spacing: 0.3px !important;
}

.card-subtitle {
  font-size: 14px !important;
  color: #6c757d !important;
  margin-top: 4px;
}

.courses-toggle-section .toggle-card {
  background: rgba(255, 255, 255, 0.9) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.1) !important;
}

.toggle-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
}

.toggle-subtitle {
  font-size: 13px !important;
  color: #6c757d !important;
  margin-top: 2px;
}

.list-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
}

.soft-button {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.soft-button:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
}

.courses-list {
  background: transparent !important;
}

.course-item {
  background: rgba(255, 255, 255, 0.9) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.1) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
  padding: 8px 16px !important;
}

.course-row {
  width: 100%;
  gap: 8px;
}

.course-name-input {
  flex-grow: 1;
}

.course-name-input >>> .v-input__control {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px !important;
}

.course-name-input >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-width: 1px !important;
}

.drag-handle {
  color: #9e9e9e !important;
  cursor: move !important;
  flex-shrink: 0;
  transition: color 0.2s;
}

.drag-handle:hover {
  color: #3989C9 !important;
}

.order-chip {
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  flex-shrink: 0;
  min-width: 40px;
}

.save-button {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #4CAF50 !important;
  border-radius: 10px !important;
  border: 1px solid rgba(76, 175, 80, 0.3) !important;
  box-shadow: 0 2px 6px rgba(76, 175, 80, 0.15) !important;
  min-width: 40px !important;
  width: 40px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.save-button:hover:not(.v-btn--disabled) {
  background: rgba(76, 175, 80, 0.25) !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25) !important;
  transform: translateY(-1px);
}

.delete-button {
  background: rgba(244, 67, 54, 0.15) !important;
  color: #f44336 !important;
  border-radius: 10px !important;
  border: 1px solid rgba(244, 67, 54, 0.3) !important;
  box-shadow: 0 2px 6px rgba(244, 67, 54, 0.15) !important;
  min-width: 40px !important;
  width: 40px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.delete-button:hover:not(.v-btn--disabled) {
  background: rgba(244, 67, 54, 0.25) !important;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.25) !important;
  transform: translateY(-1px);
}

.delete-button .v-icon,
.save-button .v-icon {
  font-size: 20px !important;
}

.arrow-button {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border-radius: 10px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.12) !important;
  backdrop-filter: blur(10px) !important;
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.arrow-button:hover:not(.v-btn--disabled) {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 1) !important;
}

.arrow-button.v-btn--disabled {
  background: rgba(245, 245, 245, 0.6) !important;
  color: rgba(158, 158, 158, 0.5) !important;
  border-color: rgba(158, 158, 158, 0.1) !important;
  box-shadow: none !important;
  opacity: 0.6 !important;
}

.arrow-button .v-icon {
  font-size: 22px !important;
  color: inherit !important;
}

/* OFF state - gray */
.courses-switch::v-deep .v-input--switch__track {
  background-color: rgba(230, 230, 230, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(189, 189, 189, 0.5) !important;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05) !important;
}

.courses-switch::v-deep .v-input--switch__thumb {
  background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(255, 255, 255, 0.9) !important;
}

/* ON state - green */
.courses-switch.primary--text::v-deep .v-input--switch__track,
.courses-switch::v-deep .v-input--is-dirty .v-input--switch__track,
.courses-switch::v-deep .theme--light.v-input--is-dirty .v-input--switch__track {
  background: linear-gradient(135deg, #81C784 0%, #66BB6A 100%) !important;
  backdrop-filter: blur(12px) !important;
  border-color: rgba(76, 175, 80, 0.8) !important;
  box-shadow: inset 0 2px 6px rgba(76, 175, 80, 0.3), 0 2px 10px rgba(76, 175, 80, 0.3) !important;
}

.courses-switch.primary--text::v-deep .v-input--switch__thumb,
.courses-switch::v-deep .v-input--is-dirty .v-input--switch__thumb,
.courses-switch::v-deep .theme--light.v-input--is-dirty .v-input--switch__thumb {
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%) !important;
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.6), 0 2px 6px rgba(0, 0, 0, 0.2) !important;
  border-color: rgba(255, 255, 255, 1) !important;
}

.empty-state {
  background: rgba(255, 255, 255, 0.5) !important;
  border-radius: 12px !important;
  border: 2px dashed rgba(57, 137, 201, 0.2) !important;
}

@media (max-width: 960px) {
  .courses-tab {
    padding: 16px;
  }

  .course-row {
    flex-wrap: wrap;
  }

  .course-name-input {
    max-width: 100%;
  }
}
</style>
